import type {
  CreateCustomerVisitSessionRequest,
  CustomerGroupBuyRequestMutationResult,
  CustomerSelfServiceBootstrapReadModel,
  CustomerSelfServiceMutationResult,
  CustomerSelfServiceWorkspace,
  CustomerVisitSessionReadModel,
  CustomerVisitSessionRevokeRequest,
  CustomerVisitSessionRevokeResult,
  SelfServiceCustomerReservationCancelRequest,
  SelfServiceCustomerReservationCreateRequest,
  SelfServiceGroupBuyRequestCreateRequest,
} from "@billiard/contracts";
import {
  isCreateCustomerVisitSessionRequest,
  isCustomerGroupBuyRequestMutationResult,
  isCustomerSelfServiceBootstrapReadModel,
  isCustomerSelfServiceMutationResult,
  isCustomerSelfServiceWorkspace,
  isCustomerVisitSessionReadModel,
  isCustomerVisitSessionRevokeRequest,
  isCustomerVisitSessionRevokeResult,
  isSelfServiceCustomerReservationCancelRequest,
  isSelfServiceCustomerReservationCreateRequest,
  isSelfServiceGroupBuyRequestCreateRequest,
} from "@billiard/contracts";

const gatewayUrl = import.meta.env.VITE_GATEWAY_URL ?? "http://127.0.0.1:4100";
const stableIdPattern = /^[A-Za-z0-9][A-Za-z0-9._:-]{0,127}$/u;
const idempotencyKeyPattern = /^[A-Za-z0-9][A-Za-z0-9._:-]{7,127}$/u;
const customerCsrfTokenPattern = /^[A-Za-z0-9_-]{43}$/u;

export class CustomerSelfServiceApiError extends Error {
  public readonly name = "CustomerSelfServiceApiError";

  public constructor(
    public readonly code: string,
    message: string,
    public readonly status?: number,
    public readonly requestId?: string,
  ) {
    super(message);
  }

  public get statusCode(): number | undefined {
    return this.status;
  }
}

interface CustomerRequestOptions {
  method: "GET" | "POST";
  body?: unknown;
  csrfToken?: string;
  credentials?: RequestCredentials;
  purpose?: "CUSTOMER_ENTRY_BOOTSTRAP";
  signal?: AbortSignal;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return value !== null && typeof value === "object" && !Array.isArray(value);
}

function hasExactKeys(value: Record<string, unknown>, keys: readonly string[]): boolean {
  return (
    Object.keys(value).length === keys.length && keys.every((key) => Object.hasOwn(value, key))
  );
}

function explicitErrorBody(
  value: unknown,
): { code: string; message: string; requestId: string } | null {
  if (
    !isRecord(value) ||
    !hasExactKeys(value, ["code", "message", "requestId"]) ||
    typeof value.code !== "string" ||
    value.code.length < 1 ||
    value.code.length > 128 ||
    typeof value.message !== "string" ||
    value.message.length < 1 ||
    value.message.length > 1_000 ||
    typeof value.requestId !== "string" ||
    !stableIdPattern.test(value.requestId)
  ) {
    return null;
  }
  return { code: value.code, message: value.message, requestId: value.requestId };
}

function requireCustomerCsrfToken(csrfToken: string): void {
  if (!customerCsrfTokenPattern.test(csrfToken)) {
    throw new CustomerSelfServiceApiError(
      "CUSTOMER_SELF_SERVICE_CSRF_TOKEN_INVALID",
      "当前顾客会话的安全凭证无效，系统没有发送操作，请重新进入顾客自助页",
      400,
    );
  }
}

async function requestCustomerJson(
  path: string,
  options: CustomerRequestOptions,
): Promise<unknown> {
  if (options.method === "POST") requireCustomerCsrfToken(options.csrfToken ?? "");
  const headers: Record<string, string> = { accept: "application/json" };
  if (options.method === "POST") {
    headers["content-type"] = "application/json";
    headers["x-customer-csrf-token"] = options.csrfToken ?? "";
  }

  let response: Response;
  try {
    const timeoutSignal = AbortSignal.timeout(5_000);
    response = await fetch(`${gatewayUrl}${path}`, {
      method: options.method,
      cache: "no-store",
      credentials: options.credentials ?? "include",
      headers,
      ...(options.body === undefined ? {} : { body: JSON.stringify(options.body) }),
      signal: options.signal ? AbortSignal.any([timeoutSignal, options.signal]) : timeoutSignal,
    });
  } catch {
    if (options.purpose === "CUSTOMER_ENTRY_BOOTSTRAP") {
      throw new CustomerSelfServiceApiError(
        "CUSTOMER_SELF_SERVICE_BOOTSTRAP_UNAVAILABLE",
        "暂时无法读取门店顾客入口，系统未执行任何操作。请恢复连接后重试",
      );
    }
    throw new CustomerSelfServiceApiError(
      "CUSTOMER_SELF_SERVICE_GATEWAY_UNREACHABLE",
      "门店连接中断，操作结果尚未确认，请先查询原请求，不要重复提交",
    );
  }

  let body: unknown;
  try {
    body = await response.json();
  } catch {
    if (options.purpose === "CUSTOMER_ENTRY_BOOTSTRAP") {
      throw new CustomerSelfServiceApiError(
        "CUSTOMER_SELF_SERVICE_BOOTSTRAP_RESPONSE_INVALID",
        "门店顾客入口返回内容无法安全确认，系统未执行任何操作。请恢复连接后重试",
        response.status,
      );
    }
    throw new CustomerSelfServiceApiError(
      "CUSTOMER_SELF_SERVICE_RESPONSE_INVALID",
      "门店返回内容无法安全确认，操作结果暂时未知",
      response.status,
    );
  }
  if (!response.ok) {
    const explicit = explicitErrorBody(body);
    if (!explicit) {
      if (options.purpose === "CUSTOMER_ENTRY_BOOTSTRAP") {
        throw new CustomerSelfServiceApiError(
          "CUSTOMER_SELF_SERVICE_BOOTSTRAP_RESPONSE_INVALID",
          "门店顾客入口失败信息无法安全确认，系统未执行任何操作。请恢复连接后重试",
          response.status,
        );
      }
      throw new CustomerSelfServiceApiError(
        "CUSTOMER_SELF_SERVICE_RESPONSE_INVALID",
        "门店返回的失败信息无法安全确认，操作结果暂时未知",
        response.status,
      );
    }
    throw new CustomerSelfServiceApiError(
      explicit.code,
      explicit.message,
      response.status,
      explicit.requestId,
    );
  }
  return body;
}

function invalidRequest(message: string): never {
  throw new CustomerSelfServiceApiError("CUSTOMER_SELF_SERVICE_REQUEST_INVALID", message, 400);
}

function invalidResponse(message: string): never {
  throw new CustomerSelfServiceApiError("CUSTOMER_SELF_SERVICE_RESPONSE_INVALID", message, 200);
}

function expectedMaskedMobile(mobile: string): string {
  return `${mobile.slice(0, 3)}****${mobile.slice(-4)}`;
}

export async function getCustomerSelfServiceBootstrap(
  signal?: AbortSignal,
): Promise<CustomerSelfServiceBootstrapReadModel> {
  const value = await requestCustomerJson("/v1/customer-self-service/bootstrap", {
    method: "GET",
    credentials: "omit",
    purpose: "CUSTOMER_ENTRY_BOOTSTRAP",
    ...(signal ? { signal } : {}),
  });
  if (!isCustomerSelfServiceBootstrapReadModel(value)) {
    throw new CustomerSelfServiceApiError(
      "CUSTOMER_SELF_SERVICE_BOOTSTRAP_RESPONSE_INVALID",
      "门店顾客入口信息无法安全确认，系统未执行任何操作。请恢复连接后重试",
      200,
    );
  }
  return value;
}

export async function createCustomerVisitSession(
  request: CreateCustomerVisitSessionRequest,
  signal?: AbortSignal,
): Promise<CustomerVisitSessionReadModel> {
  if (!isCreateCustomerVisitSessionRequest(request)) {
    return invalidRequest("姓名、手机号或本机安全信息不完整，系统没有创建顾客会话");
  }
  const value = await requestCustomerJson("/v1/customer-self-service/session", {
    method: "POST",
    body: request,
    csrfToken: request.csrfToken,
    ...(signal ? { signal } : {}),
  });
  if (
    !isCustomerVisitSessionReadModel(value) ||
    value.status !== "ACTIVE" ||
    value.tenantId !== request.tenantId ||
    value.storeId !== request.storeId ||
    value.customerName !== request.customerName ||
    value.customerMobileMasked !== expectedMaskedMobile(request.customerMobile)
  ) {
    return invalidResponse("新建顾客会话与本次姓名、手机号或门店不一致，页面已停止使用该回执");
  }
  return value;
}

export async function getCustomerSelfServiceWorkspace(
  signal?: AbortSignal,
): Promise<CustomerSelfServiceWorkspace> {
  const value = await requestCustomerJson("/v1/customer-self-service/workspace", {
    method: "GET",
    ...(signal ? { signal } : {}),
  });
  if (!isCustomerSelfServiceWorkspace(value)) {
    return invalidResponse("顾客服务工作区包含无法确认或不属于当前会话的内容，页面已停止显示");
  }
  return value;
}

function assertGroupBuyRequestResultMatchesRequest(
  value: unknown,
  request: SelfServiceGroupBuyRequestCreateRequest,
): CustomerGroupBuyRequestMutationResult {
  if (
    !isCustomerGroupBuyRequestMutationResult(value) ||
    value.operation !== "CREATE" ||
    value.request.provider !== request.provider ||
    value.request.voucherCodeSuffix !== request.voucherCodeSuffix ||
    value.request.note !== request.note
  ) {
    return invalidResponse(
      "团购请求回执与本次平台、券码尾号或说明不一致，结果暂时未知，请先查询原请求",
    );
  }
  return value;
}

export async function createSelfServiceGroupBuyRequest(
  request: SelfServiceGroupBuyRequestCreateRequest,
  csrfToken: string,
  signal?: AbortSignal,
): Promise<CustomerGroupBuyRequestMutationResult> {
  if (!isSelfServiceGroupBuyRequestCreateRequest(request)) {
    return invalidRequest("团购平台、券码尾号或说明不完整，系统没有提交请求");
  }
  const value = await requestCustomerJson("/v1/customer-self-service/group-buy-requests", {
    method: "POST",
    body: request,
    csrfToken,
    ...(signal ? { signal } : {}),
  });
  return assertGroupBuyRequestResultMatchesRequest(value, request);
}

export async function getCustomerGroupBuyRequestByRequest(
  idempotencyKey: string,
  signal?: AbortSignal,
): Promise<CustomerGroupBuyRequestMutationResult | null> {
  if (!idempotencyKeyPattern.test(idempotencyKey)) {
    return invalidRequest("本机保存的团购请求编号无效，系统没有查询或重试");
  }
  try {
    const value = await requestCustomerJson(
      `/v1/customer-self-service/group-buy-requests/by-request/${encodeURIComponent(idempotencyKey)}`,
      { method: "GET", ...(signal ? { signal } : {}) },
    );
    if (!isCustomerGroupBuyRequestMutationResult(value)) {
      return invalidResponse("原团购请求的查询回执无法安全确认，结果仍然未知");
    }
    return value;
  } catch (error) {
    if (
      error instanceof CustomerSelfServiceApiError &&
      error.status === 404 &&
      error.code === "CUSTOMER_GROUP_BUY_REQUEST_RESULT_NOT_FOUND"
    ) {
      return null;
    }
    throw error;
  }
}

function expectedReservationEndAt(requestedStartAt: string, durationMinutes: number): string {
  return new Date(Date.parse(requestedStartAt) + durationMinutes * 60_000).toISOString();
}

function assertCreateResultMatchesRequest(
  value: unknown,
  request: SelfServiceCustomerReservationCreateRequest,
): CustomerSelfServiceMutationResult {
  if (
    !isCustomerSelfServiceMutationResult(value) ||
    value.operation !== "CREATE" ||
    value.reservation.partySize !== request.partySize ||
    value.reservation.requestedStartAt !== request.requestedStartAt ||
    value.reservation.requestedEndAt !==
      expectedReservationEndAt(request.requestedStartAt, request.durationMinutes) ||
    value.reservation.note !== request.note
  ) {
    return invalidResponse("预约回执与本次人数、时间或备注不一致，结果暂时未知，请先查询原请求");
  }
  return value;
}

export async function createSelfServiceReservation(
  request: SelfServiceCustomerReservationCreateRequest,
  csrfToken: string,
  signal?: AbortSignal,
): Promise<CustomerSelfServiceMutationResult> {
  if (!isSelfServiceCustomerReservationCreateRequest(request)) {
    return invalidRequest("预约人数、时间、时长或备注不完整，系统没有提交预约");
  }
  const value = await requestCustomerJson("/v1/customer-self-service/reservations", {
    method: "POST",
    body: request,
    csrfToken,
    ...(signal ? { signal } : {}),
  });
  return assertCreateResultMatchesRequest(value, request);
}

export async function getCustomerSelfServiceMutationByRequest(
  idempotencyKey: string,
  signal?: AbortSignal,
): Promise<CustomerSelfServiceMutationResult | null> {
  if (!idempotencyKeyPattern.test(idempotencyKey)) {
    return invalidRequest("本机保存的原请求编号无效，系统没有查询或重试");
  }
  try {
    const value = await requestCustomerJson(
      `/v1/customer-self-service/reservations/by-request/${encodeURIComponent(idempotencyKey)}`,
      { method: "GET", ...(signal ? { signal } : {}) },
    );
    if (!isCustomerSelfServiceMutationResult(value)) {
      return invalidResponse("原预约请求的查询回执无法安全确认，结果仍然未知");
    }
    return value;
  } catch (error) {
    if (
      error instanceof CustomerSelfServiceApiError &&
      error.status === 404 &&
      error.code === "CUSTOMER_SELF_SERVICE_REQUEST_RESULT_NOT_FOUND"
    ) {
      return null;
    }
    throw error;
  }
}

export async function cancelSelfServiceReservation(
  reservationId: string,
  request: SelfServiceCustomerReservationCancelRequest,
  csrfToken: string,
  signal?: AbortSignal,
): Promise<CustomerSelfServiceMutationResult> {
  if (
    !stableIdPattern.test(reservationId) ||
    !isSelfServiceCustomerReservationCancelRequest(request)
  ) {
    return invalidRequest("预约记录、当前版本或取消原因不完整，系统没有取消预约");
  }
  const value = await requestCustomerJson(
    `/v1/customer-self-service/reservations/${encodeURIComponent(reservationId)}/cancel`,
    {
      method: "POST",
      body: request,
      csrfToken,
      ...(signal ? { signal } : {}),
    },
  );
  if (
    !isCustomerSelfServiceMutationResult(value) ||
    value.operation !== "CANCEL" ||
    value.reservation.reservationId !== reservationId ||
    value.reservation.reservationVersion !== request.expectedReservationVersion + 1
  ) {
    return invalidResponse("取消回执与所选预约或确认前版本不一致，结果暂时未知，请先查询原请求");
  }
  return value;
}

export async function revokeCustomerVisitSession(
  request: CustomerVisitSessionRevokeRequest,
  csrfToken: string,
  signal?: AbortSignal,
): Promise<CustomerVisitSessionRevokeResult> {
  if (!isCustomerVisitSessionRevokeRequest(request)) {
    return invalidRequest("退出请求编号无效，系统没有退出当前顾客会话");
  }
  const value = await requestCustomerJson("/v1/customer-self-service/session/revoke", {
    method: "POST",
    body: request,
    csrfToken,
    ...(signal ? { signal } : {}),
  });
  if (!isCustomerVisitSessionRevokeResult(value)) {
    return invalidResponse("退出回执无法确认属于一个已退出的顾客会话，请刷新后核对");
  }
  return value;
}
